import { Component, OnDestroy } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams, IRowNode } from 'ag-grid-community';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { BusinessAccount } from '@app/core/client/store/client.model';

import { SnackBarConfig } from '../../../shared/configs';
import { ClientApiService } from '../../../shared/services/api/client/client-api.service';
import { trackById } from '../../../shared/util';

export interface IAgGridSearchCompanyAutocompleteCellEditorParams
  extends ICellEditorParams {
  selectItemEvent :(
    currentItem :BusinessAccount | null,
    previousItem :BusinessAccount | null,
    node :IRowNode,
  ) => void;
}

@Component({
  selector: 'lum-ag-grid-search-company-autocomplete-cell-editor',
  templateUrl: './ag-grid-search-company-autocomplete-cell-editor.component.html',
  styleUrls: ['./ag-grid-search-company-autocomplete-cell-editor.component.scss'],
})
export class AgGridSearchCompanyAutocompleteCellEditorComponent
  implements ICellEditorAngularComp, OnDestroy
{
  selectedItem :BusinessAccount | null = null;
  previousSelectedItem :BusinessAccount | null = null;

  trackById = trackById;

  private readonly _items = new BehaviorSubject<BusinessAccount[]>(
    <BusinessAccount[]>[],
  );
  items$ = this._items.asObservable();

  private readonly _loadingItems = new BehaviorSubject<boolean>(<boolean>false);
  loadingItems$ = this._loadingItems.asObservable();

  private params :IAgGridSearchCompanyAutocompleteCellEditorParams;
  private onDestroy$ = new Subject();

  constructor(
    private snackBar :MatSnackBar,
    private clientApiService :ClientApiService,
  ) {}

  agInit(params :IAgGridSearchCompanyAutocompleteCellEditorParams) :void {
    this.params = params;
    this.selectedItem = params.data?.lumCompany || null;
    this.previousSelectedItem = this.selectedItem;
  }

  getValue() :BusinessAccount | null {
    return this.selectedItem;
  }

  refresh(params :IAgGridSearchCompanyAutocompleteCellEditorParams) :boolean {
    this.selectedItem = params.data?.lumCompany || null;

    return true;
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSelectItem(item :BusinessAccount) :void {
    this.selectedItem = item;
    this.params.selectItemEvent(
      this.selectedItem,
      this.previousSelectedItem,
      this.params.node,
    );
    this.params.api.stopEditing();
  }

  onInputChange(keyword :string) :void {
    this.search(keyword);
  }

  displaySelectedItem() :string {
    return this.formattedSelectedItem;
  }

  removeSelectedItem() :void {
    this.selectedItem = null;
    this.params.selectItemEvent(
      this.selectedItem,
      this.previousSelectedItem,
      this.params.node,
    );
    this.params.api.stopEditing();
  }

  private search(keyword :string) :void {
    this._loadingItems.next(true);
    this.clientApiService
      .getV2({ businessName: keyword })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this._loadingItems.next(false);
        }),
      )
      .subscribe({
        next: (response) => {
          this._items.next(response.data || []);
        },
        error: (error :any) => {
          this.snackBar.open('Error when seaching company.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get formattedSelectedItem() :string {
    return this.selectedItem?.businessName || '';
  }
}
